import React from 'react';
import { useSelector } from 'react-redux';
import DataLoader from 'Components/DataLoader/DataLoader';
import Filter from 'Components/Filter/Filter';
import OrbitalDataChart from 'Components/OrbitalDataChart/OrbitalDataChart';
import PropTypes from 'prop-types';
import OrbitalSwitch from 'Components/OrbitalSwitch/OrbitalSwitch';

const SalesAndAttendance = (props) => {
  // Set page title
  React.useEffect(() => {
    document.title = `Orbital${process.env.REACT_APP_STAGE === 'production' ? '' : '-dev'} - ${props.title}`;
  }, []);
  const controls = useSelector((store) => store.controlsReducer?.EuropaNew?.[props.subModule]);
  // console.log({ controls });

  const priorYears = controls?.Year?.values
    .filter((year) => parseInt(year, 10) < new Date().getFullYear()) // Filter out current year and future year (if present)
    .map((year) => {
      const yearInt = parseInt(year, 10);
      const currentYear = new Date().getFullYear();
      // console.log({ currentYear, yearInt });
      if (yearInt === currentYear - 1) return 'PY';
      return `${currentYear - yearInt}PY`;
    });

  const getChartComponent = ({ title, fields }) => (
    <OrbitalDataChart
      key={title}
      title={title}
      module="EuropaNew"
      subModule={props.subModule}
      datasetParams={props.datasetParams}
      chartDefinition={{
        xAxis: {
          fieldName: 'Date',
          modifier: controls?.TimeGrouping?.selection[0] || 'Day',
        },
        yAxis: fields
          .map(({ fieldName, secondaryAxis, type }) => [
            {
              fieldName,
              modifier: 'CY',
              markers: true,
              lineWidth: 2,
              secondaryAxis: secondaryAxis || false,
              type: type || 'scatter',
            },
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...(controls?.compYear?.selection || []).map((compYear) => ({
              fieldName,
              modifier: compYear,
              markers: false,
              lineWidth: 1,
              secondaryAxis: secondaryAxis || false,
              type: type || 'scatter',
            })),
          ])
          .flat(),
        cumulative: controls?.toggleCumulative?.selection,
        highlightWeekends: true,
        height: 400,
        widthRatio: 2,
      }}
    />
  );

  return (
    <DataLoader module="EuropaNew" subModule={props.subModule} pageTitle={props.title} datasetParams={props.datasetParams}>
      <section className="topRow">
        <article className="controls">
          <section>
            <Filter
              module="EuropaNew"
              subModule={props.subModule}
              controlName="TimeGrouping"
              placeholder="Book Year..."
              values={['Day', 'Week', 'Month', 'Quarter', 'Year']}
              width={100}
            />
            <Filter module="EuropaNew" subModule={props.subModule} controlName="compYear" placeholder="Comp Year..." width={200} values={priorYears} multiple />
            <OrbitalSwitch module="EuropaNew" subModule={props.subModule} controlName="toggleCumulative" label="Cumulative" style={{ marginTop: '5px' }} />
          </section>
          <section>
            {['Park', 'Year', 'YearMonth', 'ProductType', 'Channel'].map((filterName) => (
              <Filter
                key={filterName}
                module="EuropaNew"
                subModule={props.subModule}
                controlName={filterName}
                placeholder={`${filterName}...`}
                multiple={props.datasetParams.filterDimensions[filterName]?.singleSelect !== true}
              />
            ))}
          </section>
        </article>
      </section>
      <main style={{ display: 'flex', justifyContent: 'space-between' }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {getChartComponent({
            title: 'Sales units',
            fields: controls?.Park?.selection && controls?.Park?.selection?.length > 0 ? [{ fieldName: 'SalesUnits' }] : [{ fieldName: 'SalesUnits_AllParks' }],
          })}
          {getChartComponent({
            title: 'Sales per cap',
            fields:
              controls?.Park?.selection && controls?.Park?.selection?.length > 0 ? [{ fieldName: 'SalesPerCap' }] : [{ fieldName: 'SalesPerCap_AllParks' }],
          })}
          {getChartComponent({ title: 'Sales Revenues', fields: [{ fieldName: 'SalesRevenues' }] })}
        </section>
        <section style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {getChartComponent({
            title: 'Attendance',
            fields: [{ fieldName: 'Attendance' }],
          })}
          {getChartComponent({ title: 'Weather Code', fields: [{ fieldName: 'WeatherCode_weighed' }] })}
          {getChartComponent({ title: 'Admission per cap', fields: [{ fieldName: 'AdmissionPerCap' }] })}
          {getChartComponent({ title: 'Admission Revenues', fields: [{ fieldName: 'AdmissionRevenues' }] })}
        </section>
      </main>
    </DataLoader>
  );
};

SalesAndAttendance.propTypes = {
  title: PropTypes.string.isRequired,
  subModule: PropTypes.string.isRequired,
  datasetParams: PropTypes.shape({
    mainFile: PropTypes.string.isRequired,
    runDetailsFile: PropTypes.string.isRequired,
    tableName: PropTypes.string,
    mainDateField: PropTypes.string,
    headersRemaps: PropTypes.objectOf(PropTypes.string),
    calculatedMetrics: PropTypes.shape({
      numerator: PropTypes.string,
      denominator: PropTypes.string,
    }),
    metrics: PropTypes.shape({
      fieldName: PropTypes.string,
      displayName: PropTypes.string,
      chartFormat: PropTypes.string,
      format: PropTypes.shape({
        style: PropTypes.string,
        maximumFractionDigits: PropTypes.number,
      }),
    }),
    transformRow: PropTypes.func,
    filterDimensions: PropTypes.objectOf(PropTypes.object),
  }).isRequired,
};

export default SalesAndAttendance;
